import styled from 'styled-components'
import { breakpoints } from '@src/constants/breakpoints'

export const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.grey};
  padding-bottom: 0px;
`

export const SplitLayout = styled.div`
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 520px;

  @media (max-width: ${breakpoints.largeTabletMisc}px) {
    flex-direction: column;
    min-height: 0;
  }
`

export const Aside = styled.aside`
  width: 400px;
  flex: none;
  background: ${({ theme }) => theme.colors.brand};
  color: white;
  padding: 34px 36px;
  display: flex;
  flex-direction: column;

  @media (max-width: ${breakpoints.largeTabletMisc}px) {
    width: auto;
    padding: 22px 20px 24px;
  }
`

export const Logo = styled.img`
  height: 64px;
  width: auto;
  object-fit: contain;
  object-position: left center;

  @media (max-width: ${breakpoints.largeTabletMisc}px) {
    height: 56px;
    margin: 0 auto;
    object-position: center;
  }
`

export const AsideBody = styled.div`
  margin-top: auto;

  @media (max-width: ${breakpoints.largeTabletMisc}px) {
    margin-top: 18px;
  }
`

export const Kicker = styled.div`
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.brandLight};
`

export const AsideTitle = styled.h2`
  font-size: 30px;
  line-height: 1.1;
  font-weight: 700;
  letter-spacing: -0.015em;
  margin: 14px 0 0;
  color: white;
`

export const AsideText = styled.p`
  font-size: 14.5px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.78);
  margin: 16px 0 0;
`

export const AppBadges = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 30px;

  img,
  svg {
    height: 46px;
    width: auto;
    display: block;
  }

  @media (max-width: ${breakpoints.largeTabletMisc}px) {
    gap: 8px;
    margin-top: 16px;

    a {
      flex: 1;
      display: block;
    }

    img,
    svg {
      height: 42px;
      width: 100%;
      object-fit: contain;
    }
  }
`

export const Main = styled.main`
  flex: 1;
  min-width: 0;
  background: white;
  padding: 88px 48px 44px;
  display: flex;
  flex-direction: column;

  & > * {
    width: 100%;
    max-width: 520px;
  }

  @media (max-width: ${breakpoints.largeTabletMisc}px) {
    padding: 26px 20px 28px;

    & > * {
      max-width: none;
    }
  }
`

export const PageTitle = styled.h1`
  font-size: 36px;
  line-height: 1.08;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.darkText};
  margin: 0;

  @media (max-width: ${breakpoints.largeTabletMisc}px) {
    font-size: 26px;
    line-height: 1.12;
    letter-spacing: -0.015em;
  }
`

export const Lede = styled.p`
  font-size: 16.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.mainText};
  opacity: 0.7;
  margin: 16px 0 0;

  @media (max-width: ${breakpoints.largeTabletMisc}px) {
    font-size: 15px;
    margin-top: 12px;
  }
`

export const StepIndicator = styled.div<{ $done?: boolean }>`
  font-size: ${({ $done }) => ($done ? '16px' : '12px')};
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme, $done }) =>
    $done ? theme.colors.openStatus : theme.colors.danger};
`

export const Tail = styled.div`
  margin-top: 40px;

  @media (max-width: ${breakpoints.largeTabletMisc}px) {
    margin-top: 28px;
  }
`
export const PrimaryButton = styled.button<{ $fullWidth?: boolean }>`
  all: unset;
  cursor: pointer;
  box-sizing: border-box;
  height: 48px;
  padding: 0 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 700;
  background: ${({ theme }) => theme.colors.brand};
  color: white;
  transition: background 0.16s ease;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  &:hover {
    background: ${({ theme }) => theme.colors.brandDark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const LeadButton = styled(PrimaryButton)`
  justify-content: space-between;
  margin-top: 28px;
`

export const TextButton = styled.button`
  all: unset;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.darkText};

  &:hover {
    color: ${({ theme }) => theme.colors.brand};
  }
`

export const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 28px;

  & > button {
    flex: 1 1 200px;
    padding: 0 24px;
  }
`
export const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0 0 22px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.grey};
  }

  span {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.darkGrey};
  }
`

export const OptOutCopy = styled.p`
  font-size: 15px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.mainText};
  opacity: 0.7;
  margin: 0 0 18px;
`

export const EffectsList = styled.div`
  margin-top: 24px;
`

export const Effect = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey};
`

export const EffectMark = styled.div<{ $positive?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  background: ${({ theme, $positive }) =>
    $positive ? `${theme.colors.openStatus}20` : `${theme.colors.danger}20`};
  color: ${({ theme, $positive }) =>
    $positive ? theme.colors.openStatus : theme.colors.danger};
`

export const EffectContent = styled.div``

export const EffectTitle = styled.span`
  display: block;
  font-size: 14.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.darkText};
`

export const EffectNote = styled.span`
  display: block;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.mainText};
  opacity: 0.7;
  margin-top: 2px;
`
export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`

export const ErrorContainer = styled.div`
  padding: 24px;
  background: ${({ theme }) => `${theme.colors.danger}20`};
  border-radius: 8px;
  margin-top: 24px;

  h3 {
    color: ${({ theme }) => theme.colors.danger};
    margin: 0 0 8px;
  }

  p {
    color: ${({ theme }) => theme.colors.mainText};
    margin: 0;
  }
`
